import { Text, View, Platform } from "react-native";
import {
  ActionsContainer,
  Button,
  CheckboxItem,
  KeyboardAvoidingView,
  ScreenContainer,
  ScrollableContainer,
  TextInput,
  useErrorScroll,
} from "../../components";
import { Employment, RootStackParamList, RootStackScreenProps } from "../types";
import { useEffect, useRef, useState } from "react";
import { FlatList } from "react-native-gesture-handler";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, FetchResult } from "@apollo/client";
import { AddEmploymentsQuery } from "../AddEmploymentScreen/types";
import { ADD_EMPLOYMENT_QUERY } from "../AddEmploymentScreen/query";
import { ActivityIndicator } from "react-native-paper";
import JobTitlePickerInput, { JobTitleType } from "../../components/JobTitlePickerInput/JobTitlePickerInput";
import TextInputArea from "../../components/TextInputArea";
import { UpdateEmploymentMutationResponse, UpdateEmploymentMutationVariables } from "./types";
import { UPDATE_EMPLOYMENT_MUTATION } from "./query";
import { EmploymentRequestStatus } from "../AddEmploymentScreen/AddEmploymentScreen";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import SentForReviewModal from "../AccountScreen/SentForReviewModal";
import EffectiveDateField from "../AddEmploymentScreen/components/EffectiveDateField";
export const updateEmploymentTitle = "Update Employment";

type FormData = {
  jobTitle: string;
  jobClassification: string;
  otherJobTitle?: string;
  jobDescription: string;
  effectiveDate: string;
  hasVerifiedEligibility: boolean;
};

const UpdateEmploymentContent = ({
  employment,
  jobTitlesWithOther,
  mutationLoading,
  updateEmploymentFn,
  navigation,
}: {
  employment?: Employment;
  jobTitlesWithOther: JobTitleType[];
  mutationLoading: boolean;
  updateEmploymentFn({
    variables,
  }: {
    variables: UpdateEmploymentMutationVariables;
  }): Promise<FetchResult<UpdateEmploymentMutationResponse>>;
  navigation: NativeStackNavigationProp<RootStackParamList, "UpdateEmployment">;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sentForReviewModalVisible, setSentForReviewModalVisible] = useState<boolean>(false);

  const flatListRef = useRef<FlatList>(null);

  const defaultValues: FormData = {
    jobTitle: employment?.title || "",
    jobClassification: employment?.jobClassification || "",
    otherJobTitle: "",
    jobDescription: employment?.description || "",
    effectiveDate: "",
    hasVerifiedEligibility: false,
  };

  const onSubmit = async (data: FormData) => {
    try {
      const response = await updateEmploymentFn({
        variables: {
          input: {
            employmentId: employment?.id || "",
            jobTitle: data.jobTitle,
            jobClassification: data.jobClassification,
            jobDescription: data.jobDescription,
            effectiveDate: data.effectiveDate,
            otherJobTitle: data.otherJobTitle,
          },
        },
      });

      const { status, message, success } = response?.data?.updateEmployment || {};

      if (!success) {
        throw new Error(message);
      }

      switch (status) {
        case EmploymentRequestStatus.Pending:
          setSentForReviewModalVisible(true);
          break;
        case EmploymentRequestStatus.Approved:
          // Navigate to Account page with popover parameter for compensation flow
          if (Platform.OS === "web") {
            // For web, navigate to Account with URL query parameter
            window.location.href = "/member/account?popover=true";
          } else {
            // For mobile, use React Navigation params
            navigation.navigate("Account", { popover: "true" });
          }
          break;
        default:
          throw new Error(message);
      }
    } catch (e) {
      setErrorMessage((e as Error).message || "Something went wrong");
    }
  };

  const { Divider, scrollToField, onSubmitError } = useErrorScroll({ flatListRef, defaultValues });

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });

  const jobTitle = watch("jobTitle");

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setValue("jobDescription", "");
  }, [jobTitle, setValue]);

  return (
    <KeyboardAvoidingView>
      <SentForReviewModal
        visible={sentForReviewModalVisible}
        onClose={() => navigation.navigate("EmploymentEdit", { id: employment?.id || "" })}
      />
      <ScrollableContainer ref={flatListRef}>
        <View className="flex justify-between">
          <View className="p-6">
            <Text className="text-regular-tight font-700 text-ink-darkest">Update Employment Details</Text>
            <Text className="text-regular-normal text-ink mt-2">
              Please complete the following fields with details regarding your employment in the golf industry.
            </Text>
          </View>

          <Divider field="jobTitle" />

          <View className="p-6 justify-between mb-2">
            <Text className="text-regular-tight font-700 text-ink-darkest">Job Title</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              Select your job title from the list. If your title is not listed, select "Other" and specify your title.
            </Text>
            <Controller
              name="jobTitle"
              control={control}
              rules={{
                required: "Job Title is required",
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <JobTitlePickerInput
                  onChange={(item: JobTitleType) => {
                    setValue("jobClassification", item.jobClassification!);
                    onChange(item.jobTitle);
                  }}
                  value={value}
                  placeholder="Job Title"
                  jobTitlesData={jobTitlesWithOther}
                  error={error}
                />
              )}
            />
            {jobTitle === "Other" ? (
              <View className="mt-2">
                <Controller
                  name="otherJobTitle"
                  control={control}
                  rules={{
                    required: jobTitle === "Other" ? "Other job title is required" : false,
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextInput
                      testID="otherJobTitleTextInput"
                      onChangeText={(text) => onChange(text)}
                      value={value}
                      placeholder="Add Your Job Title"
                      error={error}
                    />
                  )}
                />
              </View>
            ) : null}
          </View>

          <Divider field="jobDescription" />

          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest">Job Description</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              Provide a list of your duties and responsibilities. Include any additional information about your facility that
              helps clarify your role and job function.
            </Text>

            <Controller
              name="jobDescription"
              control={control}
              rules={{
                required: "You must enter a job description",
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInputArea
                  onChangeText={(text) => onChange(text)}
                  value={value}
                  error={error}
                  testID="jobDescriptionTextArea"
                  placeholder="Ex. I own and operate a golf shop at a PGA Recognized Golf Facility."
                  onFocus={() => scrollToField("jobDescription")}
                />
              )}
            />
          </View>

          <Divider field="effectiveDate" />

          <EffectiveDateField control={control} />

          <Divider field="hasVerifiedEligibility" />

          <View className="p-6 align-top">
            <Controller
              name="hasVerifiedEligibility"
              control={control}
              rules={{
                required: "You must verify your eligibility",
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CheckboxItem
                  value={value}
                  testID="verifiedEligibilityCheckbox"
                  onChange={onChange}
                  label="I verify that I meet the eligible employment requirements as defined by the PGA Constitution and Bylaws for the classification and employment I have submitted. The employment must show a pattern of employment that is regular, continuous, at the place of employment and provide the public with golf related goods and/or services. Members and Associates are cautioned to be factual, as falsification of information could result in disciplinary action."
                  labelStyle="text-regular-normal text-left mx-0"
                  className="items-start px-0"
                  error={error}
                />
              )}
            />
          </View>
          <View>
            <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
              <Button
                testID="submitButton"
                loading={mutationLoading}
                size="large"
                mode="contained"
                onPress={handleSubmit(onSubmit, onSubmitError)}
                isValid={isValid}
              >
                Submit
              </Button>
            </ActionsContainer>
          </View>
        </View>
      </ScrollableContainer>
    </KeyboardAvoidingView>
  );
};

const UpdateEmploymentScreen = ({ route, navigation }: RootStackScreenProps<"UpdateEmployment">) => {
  const employmentId = route.params?.employmentId;
  const { data: employmentsData, loading } = useQuery<AddEmploymentsQuery>(ADD_EMPLOYMENT_QUERY);

  const [updateEmploymentFn, { loading: loadingUpdateEmployment }] = useMutation<
    UpdateEmploymentMutationResponse,
    UpdateEmploymentMutationVariables
  >(UPDATE_EMPLOYMENT_MUTATION);

  if (loading) return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;

  const jobTitlesWithOther: JobTitleType[] = [
    ...employmentsData!.availableJobTitles.slice().sort((a, b) => a.jobTitle.localeCompare(b.jobTitle)),
    { id: "Other", jobTitle: "Other", jobClassification: "" },
  ];

  const employment = employmentsData?.currentPerson.employments.find((employment) => employment.id === employmentId);

  return (
    <ScreenContainer navigationTitle={updateEmploymentTitle} goBack={() => navigation.goBack()}>
      <UpdateEmploymentContent
        employment={employment}
        jobTitlesWithOther={jobTitlesWithOther}
        mutationLoading={loadingUpdateEmployment}
        updateEmploymentFn={updateEmploymentFn}
        navigation={navigation}
      />
    </ScreenContainer>
  );
};

export default UpdateEmploymentScreen;
